import { Link } from "@inertiajs/react"
import { Plus, Video } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MasterclassModuleCard } from "@/components/masterclasses/masterclass-module-card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { newMasterclassModulePath } from "@/routes"
import type { MasterclassModuleRow as MasterclassModuleRowType } from "@/types/masterclasses"

/**
 * OKS-55 — Inline modules section on Masterclass Show (UX Spec §2.1).
 *
 * Replaces the OKS-54 "Coming soon" placeholder Card. Renders the
 * per-masterclass module list as a responsive grid of rich cards
 * (OKS-833 — replaces the OKS-55/620 truncated table).
 *
 * States (UX Spec §3):
 *   - Empty   — centered EmptyState w/ Video icon + "Add module" CTA
 *   - Success — card grid (1 col mobile, 2 col ≥768px — mirrors the
 *     masterclass card grid)
 *   - Soft-cap banner at `count >= cap_warning_threshold` (default 30)
 *   - Add button disabled w/ tooltip at `count >= cap_hard` (default 50)
 *
 * (Loading + Error states from UX Spec §3 don't apply — modules ride
 * the parent Show page's Inertia render. There's no async-fetch surface
 * to error against.)
 */
export interface MasterclassModulesSectionProps {
  masterclassId: number
  modules: MasterclassModuleRowType[]
  moduleCount: number
  canCreate: boolean
  capWarningThreshold: number
  capHard: number
}

export function MasterclassModulesSection({
  masterclassId,
  modules,
  moduleCount,
  canCreate,
  capWarningThreshold,
  capHard,
}: MasterclassModulesSectionProps) {
  const { t } = useTranslation()

  const showCapBanner =
    moduleCount >= capWarningThreshold && moduleCount < capHard
  const atCap = moduleCount >= capHard

  return (
    <Card data-testid="masterclass-modules-section">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle>{t("masterclasses.show.modules.title")}</CardTitle>
          {moduleCount > 0 && (
            <p
              className="text-muted-foreground text-sm"
              data-testid="masterclass-modules-count"
            >
              {t("masterclasses.show.modules.count_label", {
                count: moduleCount,
              })}
            </p>
          )}
        </div>
        <AddModuleButton
          masterclassId={masterclassId}
          canCreate={canCreate}
          atCap={atCap}
        />
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {showCapBanner && (
          <Alert
            data-testid="masterclass-modules-cap-banner"
            className="border-amber-300 bg-amber-50 text-amber-900"
          >
            <AlertDescription>
              {t("masterclasses.show.modules.cap_banner", {
                count: moduleCount,
              })}
            </AlertDescription>
          </Alert>
        )}

        {modules.length === 0 ? (
          <EmptyState
            masterclassId={masterclassId}
            canCreate={canCreate}
            atCap={atCap}
          />
        ) : (
          <ModulesGrid masterclassId={masterclassId} modules={modules} />
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------

function AddModuleButton({
  masterclassId,
  canCreate,
  atCap,
}: {
  masterclassId: number
  canCreate: boolean
  atCap: boolean
}) {
  const { t } = useTranslation()

  // The disabled-button shape needs a `<span>` wrapper for the Tooltip
  // to capture pointer events (disabled buttons swallow them).
  if (!canCreate || atCap) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span className="inline-flex" aria-disabled="true">
              <Button
                type="button"
                disabled
                variant="outline"
                data-testid="masterclass-modules-add-disabled"
              >
                <Plus className="mr-1 size-4" aria-hidden="true" />
                {t("masterclasses.show.modules.add_button")}
              </Button>
            </span>
          }
        />
        <TooltipContent>
          {t("masterclasses.show.modules.add_disabled_tooltip")}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Button asChild data-testid="masterclass-modules-add">
      <Link href={newMasterclassModulePath(masterclassId)}>
        <Plus className="mr-1 size-4" aria-hidden="true" />
        {t("masterclasses.show.modules.add_button")}
      </Link>
    </Button>
  )
}

function EmptyState({
  masterclassId,
  canCreate,
  atCap,
}: {
  masterclassId: number
  canCreate: boolean
  atCap: boolean
}) {
  const { t } = useTranslation()

  return (
    <div
      className="border-input flex flex-col items-center gap-3 rounded-md border border-dashed py-10 text-center"
      data-testid="masterclass-modules-empty"
    >
      <Video className="text-muted-foreground size-8" aria-hidden="true" />
      <p className="text-base font-medium">
        {t("masterclasses.show.modules.empty_title")}
      </p>
      <p className="text-muted-foreground max-w-md text-sm">
        {t("masterclasses.show.modules.empty_body")}
      </p>
      {canCreate && !atCap && (
        <Button asChild data-testid="masterclass-modules-empty-cta">
          <Link href={newMasterclassModulePath(masterclassId)}>
            <Plus className="mr-1 size-4" aria-hidden="true" />
            {t("masterclasses.show.modules.empty_cta")}
          </Link>
        </Button>
      )}
    </div>
  )
}

function ModulesGrid({
  masterclassId,
  modules,
}: {
  masterclassId: number
  modules: MasterclassModuleRowType[]
}) {
  const total = modules.length

  // OKS-833 — responsive card grid: 1 col on mobile, 2 col ≥768px
  // (mirrors the masterclass card grid). Each card is a container (not a
  // link) carrying its own reorder/edit/delete actions; the action cluster
  // never shrinks off-screen and long titles wrap (OKS-620 guards, carried
  // into the card from the retired table row).
  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-2"
      data-testid="masterclass-modules-grid"
    >
      {modules.map((mod, index) => (
        <MasterclassModuleCard
          key={mod.id ?? `tmp-${index}`}
          masterclassId={masterclassId}
          // `id` is non-null on every row the server emits — the only
          // null-id place is the form's seed shape, which doesn't render
          // via this component.
          module={mod as MasterclassModuleRowType & { id: number }}
          index={index}
          total={total}
        />
      ))}
    </div>
  )
}
